/**
 * Custom Site Specific Javascript
 *
 * This file is to add custom features to the website without having to modify
 * core/common assets. Define a custom class, and build new or overwrite
 * existing methods to create custom functionality.
 */

import { siteInfoLang, userLanguage } from "commonAssets/js/constants";
import { validateField } from "commonAssets/lib/validateField";
import { modal } from "commonAssets/lib/modal";
import { toggleFieldError } from "commonAssets/lib/toggleFieldError";
import Tagify from "@yaireo/tagify";
import "@yaireo/tagify/dist/tagify.css";

/**
 * Inserts the official settlement website hero notice on the home page.
 * Uses safe DOM construction (no innerHTML) for security.
 */
const insertHomeOfficialWebsiteHero = () => {
    const slug = window.location.pathname.split("/")[1] || "home";
    if (slug !== "home") return;
    if (document.querySelector("#official-settlement-website-hero")) return;

    const homePageRoot =
        document.querySelector("#output .page#home") ||
        document.querySelector("#output #home");
    if (!homePageRoot) return;

    // Get court info dynamically from CMS data
    const courtName = siteInfoLang?.CourtName || "the Court";
    const settlementName = siteInfoLang?.SiteName || "this Settlement";

    // i18n text
    const heroText =
        userLanguage === "es"
            ? `Este es el sitio web oficial del acuerdo de ${settlementName} autorizado por ${courtName}.`
            : `This is the official Settlement Website for the ${settlementName} authorized by ${courtName}.`;

    const hero = document.createElement("section");
    hero.id = "official-settlement-website-hero";
    hero.className = "mb-4";
    hero.setAttribute("role", "note");
    hero.setAttribute(
        "aria-label",
        userLanguage === "es"
            ? "Aviso del sitio web oficial del acuerdo"
            : "Official settlement website notice"
    );

    const container = document.createElement("div");
    container.className =
        "p-3 p-md-4 text-center rounded-3 border bg-body-tertiary";

    // Eligibility page link (displayed first)
    const eligibilityParagraph = document.createElement("p");
    eligibilityParagraph.className = "mb-3 text-body";

    // Official website statement (displayed second)
    const text = document.createElement("p");
    text.className = "mb-0 fs-5 fw-semibold text-body";
    text.textContent = heroText;

    const eligibilityLink = document.createElement("a");
    eligibilityLink.href = "/eligibility";
    eligibilityLink.className = "fw-semibold text-success text-decoration-none";
    eligibilityLink.style.cssText =
        "transition: text-decoration 0.2s ease; border-bottom: 2px solid currentColor;";
    eligibilityLink.textContent =
        userLanguage === "es"
            ? "Herramienta de Elegibilidad"
            : "Eligibility Tool";

    // Build the paragraph with text nodes and link
    const beforeLink =
        userLanguage === "es" ? "Revise la " : "Review the ";
    const afterLink =
        userLanguage === "es"
            ? " para obtener más información e instrucciones sobre los pasos que debe tomar, si corresponde."
            : " for more information and instructions on what steps, if any, you need to take.";

    eligibilityParagraph.appendChild(document.createTextNode(beforeLink));
    eligibilityParagraph.appendChild(eligibilityLink);
    eligibilityParagraph.appendChild(document.createTextNode(afterLink));

    // Language switcher line
    const langParagraph = document.createElement("p");
    langParagraph.className = "mb-0 mt-3";
    const langLink = document.createElement("a");
    if (userLanguage === "es") {
        langLink.href = "/en";
        langLink.textContent = "click here";
        langParagraph.appendChild(document.createTextNode("For English, "));
        langParagraph.appendChild(langLink);
        langParagraph.appendChild(document.createTextNode("."));
    } else {
        langLink.href = "/es";
        langLink.textContent = "haga clic aquí";
        langParagraph.appendChild(
            document.createTextNode("Para español, "),
        );
        langParagraph.appendChild(langLink);
        langParagraph.appendChild(document.createTextNode("."));
    }

    container.appendChild(eligibilityParagraph);
    container.appendChild(text);
    container.appendChild(langParagraph);
    hero.appendChild(container);

    homePageRoot.insertAdjacentElement("beforebegin", hero);
};

/**
 * Restructures the #ImportantDates section on the home page into a two-column
 * Bootstrap row: Important Dates card (left) and case image (right).
 */
const restructureImportantDates = () => {
    const wrapper = document.getElementById("ImportantDates");
    if (!wrapper) return;
    // Only run on home page
    const slug = window.location.pathname.split("/")[1] || "home";
    if (slug !== "home") return;
    // Guard: don't restructure twice
    if (wrapper.querySelector(".important-dates-row")) return;

    const card = document.getElementById("importantDatesCard");
    if (!card) return;

    // Remove the old centering constraints
    wrapper.classList.remove("mx-auto");
    wrapper.style.maxWidth = "";

    // Build the row
    const row = document.createElement("div");
    row.className = "row g-4 important-dates-row";

    // Left column: Important Dates card
    const leftCol = document.createElement("div");
    leftCol.className = "col-12 col-md-6";
    leftCol.appendChild(card);

    // Right column: case image, bottom-right aligned
    const rightCol = document.createElement("div");
    rightCol.className =
        "col-12 col-md-6 d-flex align-items-center justify-content-center";

    const img = document.createElement("img");
    img.src =
        "https://angeion-public.s3.us-east-1.amazonaws.com/www.bigbadcoughsettlement.com/assets/img/BigBadCoughAdImage.png";
    img.alt =
        userLanguage === "es"
            ? "Imagen del acuerdo DeCostanzo v. GlaxoSmithKline"
            : "DeCostanzo v. GlaxoSmithKline settlement image";
    img.className = "img-fluid";
    img.style.maxHeight = "300px";
    img.style.objectFit = "contain";

    rightCol.appendChild(img);
    row.appendChild(leftCol);
    row.appendChild(rightCol);
    wrapper.appendChild(row);
};

/**
 * Initializes custom features after DOM content loads.
 * Uses MutationObserver to handle SPA navigation.
 */
const initCustomFeatures = () => {
    const output = document.querySelector("#output");
    if (!output) return;

    const runFeatures = () => {
        insertHomeOfficialWebsiteHero();
        restructureImportantDates();
    };

    runFeatures();

    const observer = new MutationObserver(runFeatures);
    observer.observe(output, { childList: true, subtree: true });
};

document.addEventListener("DOMContentLoaded", initCustomFeatures);

/**
 * Initializes a claim object.
 *
 * @param {Object} claim - The claim object to be initialized.
 * @returns {Object} The initialized claim object.
 */
export function customInitClaim(claim) {
    // Remove default title and instructions for "claim fields" section since we have custom instructions in the form definition
    document.querySelector("#claimFieldsTitle")?.remove();
    document.querySelector("#claimFieldsBody")?.remove();

    // Disable first/last name if already populated (returning class member)
    const firstName = document.getElementById("first_name");
    const lastName = document.getElementById("last_name");
    if (firstName?.value) firstName.disabled = true;
    if (lastName?.value) lastName.disabled = true;

    // Replace native date picker with Pikaday for vaccination_date
    // Enforces class period: May 20, 2016 – May 20, 2020
    // Uses YYYY-MM-DD format so loadMember can hydrate native date inputs on return
    const vaccinationDate = document.getElementById("vaccination_date");
    if (vaccinationDate) {
        vaccinationDate.type = "text";
        vaccinationDate.placeholder = "YYYY-MM-DD";
        vaccinationDate.setAttribute("autocomplete", "off");

        new Pikaday({
            field: vaccinationDate,
            format: "YYYY-MM-DD",
            minDate: new Date("2016-05-20"),
            maxDate: new Date("2020-05-20"),
            defaultDate: new Date("2020-05-20"),
            toString: function (date) {
                const y = date.getFullYear();
                let m = date.getMonth() + 1;
                let d = date.getDate();
                if (m < 10) m = "0" + m;
                if (d < 10) d = "0" + d;
                return y + "-" + m + "-" + d;
            },
        });
    }

    // Hide documentation upload widget by default;
    // show only when claimant selects "WITH PROOF"
    const documentation = document.getElementById("documentation");
    if (documentation) {
        documentation.classList.add("d-none");

        document
            .querySelectorAll('input[name="proof_submission_type"]')
            .forEach((radio) => {
                radio.addEventListener("change", (e) => {
                    documentation.classList.toggle(
                        "d-none",
                        e.target.value.includes("WITHOUT"),
                    );
                });
            });

        // Returning members: radio may already be checked by loadMember
        // (.checked = true doesn't fire change), so sync visibility now
        const checkedRadio = document.querySelector(
            'input[name="proof_submission_type"]:checked',
        );
        if (checkedRadio) {
            documentation.classList.toggle(
                "d-none",
                checkedRadio.value.includes("WITHOUT"),
            );
        }
    }

    // Tagify: individuals_protected
    // Allows claimants to enter multiple names as discrete tags
    const individualsField = document.getElementById("individuals_protected");
    if (individualsField) {
        const placeholderText = {
            en: "Enter name(s), press Enter or comma to add",
            es: "Ingrese nombre(s), presione Enter o coma para agregar",
        };

        const tagify = new Tagify(individualsField, {
            delimiters: ",",
            maxTags: 10,
            placeholder: placeholderText[userLanguage],
            validate: function (tag) {
                return tag.value.trim().length >= 2;
            },
        });

        individualsField.tagify = tagify;

        tagify.on("add", () => validateField(individualsField));
        tagify.on("remove", () => validateField(individualsField));

        // Bilingual helper text below the label
        const helperText = {
            en: '<i class="bi bi-info-circle-fill text-info"></i> Enter the name of each individual you sought to protect, separated by commas or by pressing Enter.',
            es: '<i class="bi bi-info-circle-fill text-info"></i> Ingrese el nombre de cada persona que buscó proteger, separados por comas o presionando Enter.',
        };
        const label = document.querySelector(
            'label[for="individuals_protected"]',
        );
        if (label) {
            const helperDiv = document.createElement("div");
            helperDiv.className = "d-block my-1 small text-body-secondary";
            helperDiv.innerHTML = helperText[userLanguage];
            label.insertAdjacentElement("afterend", helperDiv);

            label.addEventListener("click", (e) => {
                e.preventDefault();
                tagify.DOM?.input?.focus();
            });
        }

        // Hydrate saved tags for returning members
        // Only if Tagify didn't auto-parse from the input value (avoids removeAllTags race condition)
        if (individualsField.dataset.savedValue && tagify.value.length === 0) {
            const savedValue = individualsField.dataset.savedValue;
            try {
                const parsed = JSON.parse(savedValue);
                if (Array.isArray(parsed)) {
                    const tags = parsed
                        .map((item) => item.value)
                        .filter(Boolean);
                    tagify.addTags(tags);
                }
            } catch {
                const names = savedValue
                    .split(",")
                    .map((n) => n.trim())
                    .filter((n) => n.length >= 2);
                if (names.length > 0) {
                    tagify.addTags(names);
                }
            }
        }
        delete individualsField.dataset.savedValue;

        // Clear spurious "Required" from Tagify init change event.
        // Only re-validate if returning member has tags; otherwise just clear the error.
        if (tagify.value.length > 0) {
            validateField(individualsField);
        } else {
            toggleFieldError(individualsField, false);
        }
    }

    return claim;
}

/**
 * Custom function to load a member.
 *
 * @param {Object} claim - The member object to be loaded.
 * @returns {Object} - The loaded member object.
 */
export function customLoadMember(claim) {
    console.log("[Load class member]...");

    // Stash raw value for Tagify hydration in customInitClaim
    // (Tagify isn't initialized yet at this point in the lifecycle)
    const individualsField = document.getElementById("individuals_protected");
    if (individualsField?.value) {
        individualsField.dataset.savedValue = individualsField.value;
    }

    return claim;
}

/**
 * Processes a claim for a custom member.
 *
 * @param {Object} claim - The claim to be processed.
 * @returns {Object} The processed claim.
 */
export function customProcessClaim(claim) {
    const withProof = document.getElementById("proof_submission_type_1");

    if (withProof && withProof.checked) {
        const hasNewUploads = (claim.uploads || []).length > 0;
        const hasExistingFiles =
            (claim.class_member?.claim_files || []).length > 0;

        if (!hasNewUploads && !hasExistingFiles) {
            const fileUpload = document.getElementById("file-upload");
            toggleFieldError(fileUpload, true, "Required");

            const errorMsg =
                userLanguage === "es"
                    ? "Seleccionó enviar Prueba Calificada de Vacunación. Cargue su documentación antes de enviar."
                    : "You selected to submit Qualifying Proof of Vaccination. Please upload your documentation before submitting.";

            modal(siteInfoLang?.Errors?.error || "Error", errorMsg);
            claim.flags.valid = false;
            return claim;
        }
    }

    return claim;
}
